import React from 'react';
import { Section as SectionDI, SkeletonItem as SkeletonItemDI } from '@atlaskit/side-navigation';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl as useIntlDI } from '@atlassian/jira-intl';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { SectionHeader as SectionHeaderDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/section-header/index.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlParams as matchUrlParamsDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { ConnectMenu as ConnectMenuDI } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/ui/connect-menu/main.tsx';
import { createReportUrlFunc } from '../../../../../../common/utils/index.tsx';
import { useDefaultReportItems as useDefaultReportItemsDI } from '../../../../../../controllers/default-report-items/index.tsx';
import { useReportFeatures as useReportFeaturesDI } from '../../../../../../controllers/features/index.tsx';
import messages from './messages.tsx';
import type { ReportsProps } from './types.tsx';

const useThrowDefaultReportItemsDI = withThrowOnError(useDefaultReportItemsDI);
const useThrowProjectDI = withThrowOnError(useProjectDI);
const useThrowReportFeaturesDI = withThrowOnError(useReportFeaturesDI);

export const Reports = ({
	ConnectMenu = ConnectMenuDI,
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	connectItems,
	SectionHeader = SectionHeaderDI,
	matchUrlParams = matchUrlParamsDI,
	useDefaultReportItems = useThrowDefaultReportItemsDI,
	useIntl = useIntlDI,
	useProject = useThrowProjectDI,
	useReportFeatures = useThrowReportFeaturesDI,
}: ReportsProps) => {
	return (
		<>
			<ReportsSidebar
				ConnectMenu={ConnectMenu}
				MenuItem={MenuItem}
				Section={Section}
				SkeletonItem={SkeletonItem}
				connectItems={connectItems}
				SectionHeader={SectionHeader}
				matchUrlParams={matchUrlParams}
				useDefaultReportItems={useDefaultReportItems}
				useIntl={useIntl}
				useProject={useProject}
				useReportFeatures={useReportFeatures}
			/>
		</>
	);
};

const ReportsSidebar = ({
	ConnectMenu = ConnectMenuDI,
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	connectItems,
	SectionHeader = SectionHeaderDI,
	matchUrlParams = matchUrlParamsDI,
	useDefaultReportItems = useThrowDefaultReportItemsDI,
	useIntl = useIntlDI,
	useProject = useThrowProjectDI,
	useReportFeatures = useThrowReportFeaturesDI,
}: ReportsProps) => {
	const baseUrl = '';
	const { data: defaultItems } = useDefaultReportItems();
	const { data: features } = useReportFeatures();
	const { formatMessage } = useIntl();
	const { data: project } = useProject();

	if (project == null || features == null) return <SkeletonItem />;

	const createReportUrl = createReportUrlFunc({ baseUrl, project });

	return (
		<>
			{defaultItems && defaultItems.items.length > 0 && (
				// @ts-expect-error - TS2769 - No overload matches this call.
				<Section id={defaultItems.id} key={defaultItems.id}>
					<SectionHeader>{defaultItems.name}</SectionHeader>
					{defaultItems.items &&
						defaultItems.items.map((item) => (
							<MenuItem
								key={item.id}
								analytics={{ itemId: item.id }}
								selectedOn={matchUrlParams(['reportKey'])}
								href={item.url}
							>
								{item.name != null ? item.name : null}
							</MenuItem>
						))}
				</Section>
			)}
			{features && features.hasForcastSection && (
				<Section>
					<SectionHeader>{formatMessage(messages.forecastAndManagement)}</SectionHeader>
					{features.hasTimeTracking && (
						<MenuItem
							analytics={{ itemId: 'time-tracking' }}
							href={createReportUrl('com.atlassian.jira.plugin.system.reports:time-tracking')}
							selectedOn={matchUrlParams(['reportKey'])}
						>
							{formatMessage(messages.timeTracking)}
						</MenuItem>
					)}
					{features.hasDeveloperWorkload && (
						<MenuItem
							analytics={{ itemId: 'developer-workload' }}
							href={createReportUrl('com.atlassian.jira.plugin.system.reports:developer-workload')}
							selectedOn={matchUrlParams(['reportKey'])}
						>
							{formatMessage(messages.userWorkload)}
						</MenuItem>
					)}
					{features.hasVersionWorkloadReport && (
						<MenuItem
							analytics={{ itemId: 'version-workload' }}
							href={createReportUrl(
								'com.atlassian.jira.plugins.jira-software-plugin:version-workload-report',
							)}
							selectedOn={matchUrlParams(['reportKey'])}
						>
							{formatMessage(messages.versionWorkload)}
						</MenuItem>
					)}
				</Section>
			)}
			<Section>
				<SectionHeader>{formatMessage(messages.other)}</SectionHeader>
				<MenuItem
					analytics={{ itemId: 'workloadpie' }}
					href={createReportUrl('com.atlassian.jira.ext.charting:workloadpie-report')}
					selectedOn={matchUrlParams(['reportKey'])}
				>
					{formatMessage(messages.workloadPieChart)}
				</MenuItem>

				<ConnectMenu
					items={connectItems}
					overrides={{
						BackMenuItem: {
							href: fg('reports-overview-page-migration')
								? `/jira/core/projects/${project.key}/reports`
								: `/projects/${project.key}?selectedItem=com.atlassian.jira.jira-projects-plugin:report-page`,
							title: formatMessage(messages.itemBack),
						},
					}}
				/>
			</Section>
		</>
	);
};
